import { useState, useMemo } from 'react';
import { Stack, Title, Text, Group, Box, Divider, Button, Table, Alert, Modal, Loader, UnstyledButton, Checkbox, Textarea } from '@mantine/core';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import { IconChartPie, IconAlertCircle, IconChevronUp, IconChevronDown, IconFlag, IconCircleCheck } from '@tabler/icons-react';
import { RecipeNutrition } from '../../types/mealPlan';
import { useAuth } from '../../hooks/useAuth';
import classes from './NutritionDrawer.module.css';

// Common, user-recognizable ways the nutrition data can be wrong. Selected
// options are sent verbatim as report `details`, so the labels double as the
// human-readable summary the team sees in the report email.
const NUTRITION_ISSUE_OPTIONS = [
  'Calorie count looks wrong',
  'Protein, carbs, or fat look wrong',
  'Sodium, sugar, or cholesterol looks wrong',
  'Ingredient breakdown looks off',
  'Nutrition seems way too high or too low',
];

// Numeric columns of the breakdown table that get heatmap shading. The
// ingredient-name column is excluded (it carries identity, not magnitude).
const HEATMAP_COLUMNS = [
  'calories',
  'protein',
  'carbohydrates',
  'fat',
  'saturatedFat',
  'transFat',
  'fiber',
  'sugar',
  'sodium',
  'cholesterol',
] as const;

// Per-column heatmap shading. Each cell is tinted by how much that ingredient
// contributes relative to the largest contribution in the SAME column (columns
// live on wildly different scales, so each is normalized independently, anchored
// at 0 → columnMax). A single neutral-blue tint is composited over the surface
// via alpha so it reads as "more vs less" — not good/bad — and adapts to light
// and dark mode automatically. Max alpha is capped so the dark cell numbers stay
// readable.
const HEATMAP_MAX_ALPHA = 0.5;
const heatmapStyle = (value: number, columnMax: number): React.CSSProperties => {
  if (!columnMax || value <= 0) return {};
  const intensity = Math.min(value / columnMax, 1) * HEATMAP_MAX_ALPHA;
  return { backgroundColor: `rgba(59, 130, 246, ${intensity.toFixed(3)})` };
};

// Freezes the ingredient column so it stays visible while the numeric columns
// scroll horizontally on narrow screens. The frozen cells need an OPAQUE surface
// background (the ingredient column is never heatmap-tinted, so the scrolling
// blue cells would otherwise show through) plus a soft right-edge shadow to read
// as pinned.
const stickyIngredientCol: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  backgroundColor: 'var(--mantine-color-body)',
  zIndex: 1,
  boxShadow: '4px 0 6px -4px rgba(0, 0, 0, 0.2)',
};

interface NutritionDrawerProps {
  opened: boolean;
  onClose: () => void;
  nutrition: RecipeNutrition;
  recipeName: string;
  recipeId: string;
  isOwner?: boolean;
  ingredientCount?: number;
  /** Recipe lives in the shared catalog (admin view) — breakdown generation
   * must go through the admin endpoint, not the user-recipes one. */
  isCatalogView?: boolean;
}

// Display fallback matching the backend's label precision (whole numbers for
// calories, sodium, cholesterol; one decimal for gram macros). Stored values
// normally arrive pre-rounded; this guards older docs written before source
// nutrition was normalized (e.g. USDA imports storing "20.56g" verbatim).
// The typeof guards cover runtime-missing fields the type declares required.
const round1 = (v: number): number =>
  typeof v === 'number' ? parseFloat(v.toFixed(1)) : v;
const round0 = (v: number): number =>
  typeof v === 'number' ? Math.round(v) : v;

const roundToLabelPrecision = (
  perServing: RecipeNutrition['perServing']
): RecipeNutrition['perServing'] => ({
  ...perServing,
  calories: round0(perServing.calories),
  protein: round1(perServing.protein),
  carbohydrates: round1(perServing.carbohydrates),
  fat: round1(perServing.fat),
  fiber: round1(perServing.fiber),
  sodium: round0(perServing.sodium),
  saturatedFat: round1(perServing.saturatedFat),
  transFat: round1(perServing.transFat),
  sugar: round1(perServing.sugar),
  cholesterol: round0(perServing.cholesterol),
});

export const NutritionDrawer: React.FC<NutritionDrawerProps> = ({
  opened,
  onClose,
  nutrition,
  recipeName,
  recipeId,
  isOwner = true,
  ingredientCount,
  isCatalogView = false,
}) => {
  const { breakdown } = nutrition;
  const perServing = roundToLabelPrecision(nutrition.perServing);
  const { user } = useAuth();
  const [breakdownModalOpened, setBreakdownModalOpened] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>('calories');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Report-an-issue modal state
  const [reportModalOpened, setReportModalOpened] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [reportDescription, setReportDescription] = useState('');

  const closeReportModal = () => {
    setReportModalOpened(false);
    // Reset after the close transition so the form doesn't flicker while closing
    setTimeout(() => {
      setSelectedIssues([]);
      setReportDescription('');
      setReportError(null);
      setReportSuccess(false);
    }, 200);
  };

  const handleSubmitReport = async () => {
    const details = [...selectedIssues];
    const trimmedDescription = reportDescription.trim();
    if (trimmedDescription) {
      details.push(trimmedDescription);
    }

    if (details.length === 0) {
      setReportError('Please select an issue or describe the problem.');
      return;
    }

    setReportSubmitting(true);
    setReportError(null);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/${recipeId}/report-nutrition-issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ details }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit nutrition issue report');
      }

      setReportSuccess(true);
    } catch (error) {
      console.error('Error reporting nutrition issue:', error);
      setReportError('Unable to submit your report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedBreakdown = useMemo(() => {
    const data = breakdown || nutrition.breakdown;
    if (!data || !sortColumn) return data;

    const sorted = [...data].sort((a, b) => {
      let aVal: number, bVal: number;

      switch (sortColumn) {
        case 'ingredient':
          return sortDirection === 'asc'
            ? a.ingredient.localeCompare(b.ingredient)
            : b.ingredient.localeCompare(a.ingredient);
        case 'calories':
          aVal = a.calories;
          bVal = b.calories;
          break;
        case 'protein':
          aVal = a.protein;
          bVal = b.protein;
          break;
        case 'carbohydrates':
          aVal = a.carbohydrates;
          bVal = b.carbohydrates;
          break;
        case 'fat':
          aVal = a.fat;
          bVal = b.fat;
          break;
        case 'saturatedFat':
          aVal = a.saturatedFat;
          bVal = b.saturatedFat;
          break;
        case 'transFat':
          aVal = a.transFat;
          bVal = b.transFat;
          break;
        case 'fiber':
          aVal = a.fiber;
          bVal = b.fiber;
          break;
        case 'sugar':
          aVal = a.sugar;
          bVal = b.sugar;
          break;
        case 'sodium':
          aVal = a.sodium;
          bVal = b.sodium;
          break;
        case 'cholesterol':
          aVal = a.cholesterol;
          bVal = b.cholesterol;
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [breakdown, nutrition.breakdown, sortColumn, sortDirection]);

  const handleShowBreakdown = async () => {
    // If we already have breakdown data, just open modal
    if (breakdown) {
      setBreakdownModalOpened(true);
      return;
    }

    // Otherwise, need to generate it
    setBreakdownLoading(true);
    setBreakdownError(null);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await user.getIdToken();
      const endpoint = isCatalogView
        ? `${process.env.REACT_APP_API_URL}/api/admin/catalog-recipes/${recipeId}/nutrient-breakdown`
        : `${process.env.REACT_APP_API_URL}/api/recipes/${recipeId}/nutrient-breakdown`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate nutrient breakdown');
      }

      const data = await response.json();

      // Update the nutrition object with the breakdown. When the breakdown was
      // computed from the ingredient DB, the server also recomputes the
      // per-serving totals (source "database") — apply them so the headline
      // numbers stay consistent with the breakdown rows.
      nutrition.breakdown = data.breakdown;
      if (data.nutrition?.perServing) {
        nutrition.perServing = data.nutrition.perServing;
        nutrition.source = data.nutrition.source;
      }
      setBreakdownModalOpened(true);
    } catch (error) {
      console.error('Error generating nutrient breakdown:', error);
      setBreakdownError('Unable to calculate nutrient breakdown. Please try again.');
    } finally {
      setBreakdownLoading(false);
    }
  };

  // Calculate daily value percentages (based on 2000 calorie diet)
  const getDailyValuePercentage = (nutrient: string, value: number): number | null => {
    const dailyValues: Record<string, number> = {
      fat: 78, // grams
      saturatedFat: 20, // grams
      cholesterol: 300, // mg
      sodium: 2300, // mg
      carbohydrates: 275, // grams
      fiber: 28, // grams
      protein: 50, // grams
      sugar: 50, // grams (added sugars)
    };

    const dailyValue = dailyValues[nutrient];
    if (!dailyValue) return null;

    return Math.round((value / dailyValue) * 100);
  };

  const NutrientRow = ({
    label,
    value,
    unit,
    bold = false,
    italic = false,
    showDivider = false,
    dailyValueKey
  }: {
    label: string;
    value: number;
    unit: string;
    bold?: boolean;
    italic?: boolean;
    showDivider?: boolean;
    dailyValueKey?: string;
  }) => {
    const dailyValue = dailyValueKey ? getDailyValuePercentage(dailyValueKey, value) : null;

    return (
      <>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap={4} wrap="nowrap">
            <Text size="sm" fw={bold ? 700 : 400} fs={italic ? 'italic' : 'normal'}>{label}</Text>
            {value !== undefined && (
              <Text size="sm" fw={400}>
                {value.toLocaleString()}{unit}
              </Text>
            )}
          </Group>
          {dailyValue !== null && value !== undefined && (
            <Text size="sm" fw={700} style={{ minWidth: '40px', textAlign: 'right' }}>
              {dailyValue}%
            </Text>
          )}
        </Group>
        {showDivider && <Divider my="xs" />}
      </>
    );
  };

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Title order={1} fw={900}>Nutrition Facts</Title>
          <Text size="sm" c="dimmed">{recipeName}</Text>
        </Stack>
      }
      size="md"
      growBody
      styles={{
        // Top-align the close button, then nudge it down so it centers on the
        // title's first line ("Nutrition Facts") — centering against the whole
        // title block makes it drift as the recipe-name line count varies. The
        // offset is (h1 line-height − button height) / 2, using the theme's
        // fluid h1 font size (clamp, line-height 1.2) and the 1.75rem md
        // CloseButton.
        header: { fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif", alignItems: 'flex-start' },
        close: { marginTop: 'calc((clamp(1.75rem, 1.2rem + 2.7vw, 2.5rem) * 1.2 - 1.75rem) / 2)' },
        body: { fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif" },
      }}
    >
      <Divider color="black" mb="md" />
      <Stack gap="md" style={{ flex: 1 }}>
        {/* Serving Size Header and Calories - Prominent Display */}
        <Box>
          <Text size="sm" fw={800}>Amount per serving</Text>
          <Group justify="space-between" align="baseline" mt="xs">
            <Text size="32px" fw={900}>Calories</Text>
            <Text size="32px" fw={900}>{perServing.calories}</Text>
          </Group>
          <Divider mt="sm" size="xl" color="dark" />
        </Box>

        {/* Daily Value Header */}
        <Box>
          <Group justify="flex-end">
            <Text size="xs" c="black" fw={700}>% Daily Value*</Text>
          </Group>
          <Divider mt="xs" color="dark" />
        </Box>

        {/* Core Nutrients */}
        <Stack gap={0}>
          <NutrientRow
            label="Total Fat"
            value={perServing.fat}
            unit="g"
            bold
            dailyValueKey="fat"
            showDivider
          />
          <Box>
            <Box pl="md">
              <NutrientRow
                label="Saturated Fat"
                value={perServing.saturatedFat}
                unit="g"
                dailyValueKey="saturatedFat"
                showDivider={false}
              />
            </Box>
            <Divider my="xs" />
          </Box>
          <Box>
            <Box pl="md">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Group gap={4} wrap="nowrap">
                  <Text size="sm" fw={400}><Text span fs="italic">Trans</Text> Fat</Text>
                  {perServing.transFat !== undefined && (
                    <Text size="sm" fw={400}>
                      {perServing.transFat.toLocaleString()}g
                    </Text>
                  )}
                </Group>
              </Group>
            </Box>
            <Divider my="xs" />
          </Box>
          <NutrientRow
            label="Cholesterol"
            value={perServing.cholesterol}
            unit="mg"
            bold
            dailyValueKey="cholesterol"
            showDivider
          />

          <NutrientRow
            label="Sodium"
            value={perServing.sodium}
            unit="mg"
            bold
            dailyValueKey="sodium"
            showDivider
          />

          <NutrientRow
            label="Total Carbohydrate"
            value={perServing.carbohydrates}
            unit="g"
            bold
            dailyValueKey="carbohydrates"
            showDivider
          />
          <Box>
            <Box pl="md">
              <NutrientRow
                label="Dietary Fiber"
                value={perServing.fiber}
                unit="g"
                dailyValueKey="fiber"
                showDivider={false}
              />
            </Box>
            <Divider my="xs" />
          </Box>
          <Box>
            <Box pl="md">
              <NutrientRow
                label="Total Sugars"
                value={perServing.sugar}
                unit="g"
                showDivider={false}
              />
            </Box>
          </Box>

          <Divider my="xs" />

          <NutrientRow
            label="Protein"
            value={perServing.protein}
            unit="g"
            bold
          />
        </Stack>

        <Divider size="xl" color="dark" />

        {/* Show the Math Button — kept directly under the facts so it isn't
            buried below the disclaimer text, where users were missing it. */}
        {(isOwner || breakdown) && (
          <Box>
            <Button
              leftSection={breakdownLoading ? <Loader size="1rem" color="dimmed" /> : <IconChartPie size="1rem" />}
              variant="light"
              onClick={handleShowBreakdown}
              disabled={breakdownLoading}
              fullWidth
            >
              {breakdownLoading ? 'Calculating... this may take up to a minute' : 'Nutrient breakdown by ingredient'}
            </Button>
            {breakdownError && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                color="red"
                radius="lg"
                mt="sm"
                styles={{
                  message: {
                    color: 'var(--mantine-color-red-6)',
                  },
                }}
              >
                {breakdownError}
              </Alert>
            )}
          </Box>
        )}

        {/* Daily Value Disclaimer */}
        <Text size="xs" c="dimmed">
          * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
        </Text>

        {/* Accuracy Disclaimer */}
        <Text size="xs" c="dimmed" mt="md">
          Nutritional information is estimated based on typical ingredients and may vary depending on the specific products you use. This data is for general informational purposes only and should not be considered medical or dietary advice. Please consult with a registered dietitian or healthcare professional for personalized nutrition guidance.
        </Text>

        {/* Report an Issue */}
        {user && (
          <Group justify="center" mt="auto" pt="xl" pb="md">
            <Button
              leftSection={<IconFlag size="0.9rem" />}
              variant="subtle"
              color="gray"
              size="xs"
              onClick={() => setReportModalOpened(true)}
            >
              Report an issue with this nutrition info
            </Button>
          </Group>
        )}
      </Stack>

      {/* Nutrient Breakdown Modal */}
      <Modal
        opened={breakdownModalOpened}
        onClose={() => setBreakdownModalOpened(false)}
        title={<Text fw={600} size="lg">Nutrient breakdown by ingredient</Text>}
        size="100%"
        centered
        radius="lg"
        styles={{
          content: {
            maxWidth: '1400px',
          },
        }}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Estimated nutritional contribution per serving from each ingredient
          </Text>

          {(breakdown || nutrition.breakdown) && (() => {
            const filteredBreakdown = sortedBreakdown!.filter(item =>
              item.calories > 0 ||
              item.protein > 0 ||
              item.carbohydrates > 0 ||
              item.fat > 0 ||
              item.saturatedFat > 0 ||
              item.transFat > 0 ||
              item.fiber > 0 ||
              item.sugar > 0 ||
              item.sodium > 0 ||
              item.cholesterol > 0
            );
            const omittedCount = ingredientCount ? ingredientCount - filteredBreakdown.length : 0;

            // Largest contribution per column across the visible rows — the
            // anchor for each column's heatmap ramp.
            const columnMax = HEATMAP_COLUMNS.reduce((acc, key) => {
              acc[key] = filteredBreakdown.reduce((max, item) => Math.max(max, item[key]), 0);
              return acc;
            }, {} as Record<(typeof HEATMAP_COLUMNS)[number], number>);

            return (
            <>
              <Box style={{ overflowX: 'auto' }}>
                <Table withRowBorders={false}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ ...stickyIngredientCol, zIndex: 2 }}>
                      <UnstyledButton onClick={() => handleSort('ingredient')}>
                        <Group gap={4}>
                          <Text size="sm" fw={700}>Ingredient</Text>
                          {sortColumn === 'ingredient' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('calories')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Calories</Text>
                          {sortColumn === 'calories' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('protein')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Protein (g)</Text>
                          {sortColumn === 'protein' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('carbohydrates')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Carbs (g)</Text>
                          {sortColumn === 'carbohydrates' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('fat')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Fat (g)</Text>
                          {sortColumn === 'fat' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('saturatedFat')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Sat. Fat (g)</Text>
                          {sortColumn === 'saturatedFat' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('transFat')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Trans Fat (g)</Text>
                          {sortColumn === 'transFat' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('fiber')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Fiber (g)</Text>
                          {sortColumn === 'fiber' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('sugar')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Sugar (g)</Text>
                          {sortColumn === 'sugar' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('sodium')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Sodium (mg)</Text>
                          {sortColumn === 'sodium' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>
                      <UnstyledButton onClick={() => handleSort('cholesterol')} style={{ width: '100%' }}>
                        <Group gap={4} justify="flex-end">
                          <Text size="sm" fw={700}>Cholesterol (mg)</Text>
                          {sortColumn === 'cholesterol' && (
                            sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
                          )}
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredBreakdown.map((item, index) => (
                    <Table.Tr key={index} className={classes.breakdownRow}>
                      <Table.Td style={stickyIngredientCol}>{item.ingredient}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.calories, columnMax.calories) }}>{Math.round(item.calories)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.protein, columnMax.protein) }}>{item.protein?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.carbohydrates, columnMax.carbohydrates) }}>{item.carbohydrates?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.fat, columnMax.fat) }}>{item.fat?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.saturatedFat, columnMax.saturatedFat) }}>{item.saturatedFat?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.transFat, columnMax.transFat) }}>{item.transFat?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.fiber, columnMax.fiber) }}>{item.fiber?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.sugar, columnMax.sugar) }}>{item.sugar?.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.sodium, columnMax.sodium) }}>{Math.round(item.sodium)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right', ...heatmapStyle(item.cholesterol, columnMax.cholesterol) }}>{Math.round(item.cholesterol)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {/* Total Row */}
                  <Table.Tr style={{ fontWeight: 700, borderTop: '2px solid' }}>
                    <Table.Td style={{ ...stickyIngredientCol, borderTop: '2px solid' }}>Total</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {Math.round((breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.calories, 0))}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.protein, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.carbohydrates, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.fat, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.saturatedFat, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.transFat, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.fiber, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {(breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.sugar, 0)?.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {Math.round((breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.sodium, 0))}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {Math.round((breakdown || nutrition.breakdown)!.reduce((sum, item) => sum + item.cholesterol, 0))}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
              </Box>
              {omittedCount > 0 && (
                <Text size="xs" c="dimmed">
                  {omittedCount} nutritionally negligible ingredient{omittedCount !== 1 ? 's' : ''} omitted.
                </Text>
              )}
              <Text size="xs" c="dimmed">
                This breakdown shows estimated nutritional contributions accounting for cooking methods and preparation. Values may not sum to exactly 100% due to rounding.
              </Text>
            </>
            );
          })()}

          {user && (
            <Group justify="flex-start" pt="xs">
              <Button
                leftSection={<IconFlag size="0.9rem" />}
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => setReportModalOpened(true)}
              >
                Report an issue with this nutrition info
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>

      {/* Report Nutrition Issue Modal */}
      <Modal
        opened={reportModalOpened}
        onClose={closeReportModal}
        title={<Text fw={600} size="lg">Report a nutrition issue</Text>}
        centered
        radius="lg"
        size="md"
      >
        {reportSuccess ? (
          <Stack align="center" gap="sm" py="md">
            <IconCircleCheck size="3rem" color="var(--mantine-color-teal-6)" />
            <Text fw={600} ta="center">Thanks for the heads up!</Text>
            <Text size="sm" c="dimmed" ta="center">
              We've received your report and will review the nutrition information for this recipe.
            </Text>
            <Button onClick={closeReportModal} fullWidth mt="xs">Done</Button>
          </Stack>
        ) : (
          <Stack>
            <Text size="sm" c="dimmed">
              Noticed something off about the nutrition facts for "{recipeName}"? Let us know what looks wrong and we'll take a look.
            </Text>

            <Checkbox.Group
              value={selectedIssues}
              onChange={setSelectedIssues}
              label="What looks incorrect?"
            >
              <Stack gap="xs" mt="xs">
                {NUTRITION_ISSUE_OPTIONS.map((option) => (
                  <Checkbox key={option} value={option} label={option} />
                ))}
              </Stack>
            </Checkbox.Group>

            <Textarea
              label="Additional details (optional)"
              placeholder="Tell us more about what looks incorrect"
              autosize
              minRows={3}
              maxRows={6}
              value={reportDescription}
              onChange={(event) => setReportDescription(event.currentTarget.value)}
            />

            {reportError && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                color="red"
                radius="lg"
                styles={{ message: { color: 'var(--mantine-color-red-6)' } }}
              >
                {reportError}
              </Alert>
            )}

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={closeReportModal} disabled={reportSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmitReport} loading={reportSubmitting}>
                Submit report
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </ResponsiveDrawer>
  );
};
